import {
  arrayEqualIgnoreOrder,
  booleanEqual,
  nullishEqual,
  numberEqual,
  objectEqual,
  optionalEqual,
  stringEqual,
} from "@redotech/util/equal";
import { assertNever } from "@redotech/util/type";
import {
  AddressPropertiesSegment,
  CartValueSegment,
  CheckoutProbabilitySegment,
  CheckoutTreeCheckoutExperienceNodeOptionalId,
  CheckoutTreeNarySegmentNode,
  CheckoutTreeSplitTestNodeOptionalId,
  CompoundSegmentCondition,
  CompoundSegmentGroup,
  CompoundSegmentLeaf,
  CrossBorderShipmentSegment,
  CustomerReturnRateSegment,
  CustomerTagsSegment,
  DatetimeRangeSegment,
  DeliveryDistanceMilesSegment,
  DeliveryLocationCondition,
  DeliveryLocationNarySegment,
  DeliveryLocationRule,
  DeliveryLocationRulesSegment,
  DeliveryLocationSegment,
  HistoricalOrderValueSegment,
  ItemQuantitySegment,
  LineItemPropertiesSegment,
  OrderCountSegment,
  OriginLocationSegment,
  PoBoxShipmentSegment,
  ProductCollectionsSegment,
  ProductMetafieldsSegment,
  ProductSkuSegment,
  ProductTagsSegment,
  ProductVendorSegment,
  Segment,
  SegmentableCheckoutCharacteristic,
  ShipmentWeightSegment,
  TimeOfDaySegment,
} from "./checkout-tree";

export const checkoutExperienceNodeEqual =
  objectEqual<CheckoutTreeCheckoutExperienceNodeOptionalId>({
    nodeType: stringEqual,
    checkoutExperienceId: stringEqual,
  });

export const splitTestNodeEqual = objectEqual<
  Omit<CheckoutTreeSplitTestNodeOptionalId, "edges">
>({ nodeType: stringEqual, splitTestId: stringEqual });

export const deliveryDistanceMilesSegmentEqual =
  objectEqual<DeliveryDistanceMilesSegment>({
    segmentableCharacteristic: stringEqual,
    miles: numberEqual,
    operator: stringEqual,
  });

export const orderCountSegmentEqual = objectEqual<OrderCountSegment>({
  segmentableCharacteristic: stringEqual,
  numOrders: numberEqual,
  operator: stringEqual,
});

export const historicalOrderValueSegmentEqual =
  objectEqual<HistoricalOrderValueSegment>({
    segmentableCharacteristic: stringEqual,
    amount: numberEqual,
    currency: stringEqual,
    operator: stringEqual,
  });

export const customerReturnRateSegmentEqual =
  objectEqual<CustomerReturnRateSegment>({
    segmentableCharacteristic: stringEqual,
    returnRatePercentage: numberEqual,
    operator: stringEqual,
  });

export const deliveryLocationSegmentEqual =
  objectEqual<DeliveryLocationSegment>({
    segmentableCharacteristic: stringEqual,
    locationCountry: objectEqual({
      countries: arrayEqualIgnoreOrder(),
      operator: stringEqual,
    }),
    locationProvince: optionalEqual(
      objectEqual({
        provinces: arrayEqualIgnoreOrder(),
        operator: stringEqual,
      }),
    ),
  });

export const customerTagsSegmentEqual = objectEqual<CustomerTagsSegment>({
  segmentableCharacteristic: stringEqual,
  tags: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  customerTagsSegmentNodeIdentifier: stringEqual,
});

export const productTagsSegmentEqual = objectEqual<ProductTagsSegment>({
  segmentableCharacteristic: stringEqual,
  tags: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
  productTagsSegmentNodeIdentifier: stringEqual,
});

export const productVendorSegmentEqual = objectEqual<ProductVendorSegment>({
  segmentableCharacteristic: stringEqual,
  vendors: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
});

export const productSkuSegmentEqual = objectEqual<ProductSkuSegment>({
  segmentableCharacteristic: stringEqual,
  skus: arrayEqualIgnoreOrder(),
  operator: stringEqual,
  cartOperator: stringEqual,
  requireExactMatch: booleanEqual,
});

export const itemQuantitySegmentEqual = objectEqual<ItemQuantitySegment>({
  segmentableCharacteristic: stringEqual,
  quantity: numberEqual,
  operator: stringEqual,
  exclusions: optionalEqual(
    objectEqual<ItemQuantitySegment["exclusions"]>({
      skus: optionalEqual(arrayEqualIgnoreOrder()),
    }),
  ),
});

export const originLocationSegmentEqual = objectEqual<OriginLocationSegment>({
  segmentableCharacteristic: stringEqual,
  originAddress: objectEqual<OriginLocationSegment["originAddress"]>({
    address1: nullishEqual(stringEqual),
    address2: nullishEqual(stringEqual),
    city: nullishEqual(stringEqual),
    province: nullishEqual(stringEqual),
    postalCode: nullishEqual(stringEqual),
    country: nullishEqual(stringEqual),
  }),
});

export const timeOfDaySegmentEqual = objectEqual<TimeOfDaySegment>({
  segmentableCharacteristic: stringEqual,
  time: objectEqual({ hh: numberEqual, mm: numberEqual }),
  ianaTimezone: stringEqual,
  operator: stringEqual,
  weekdays: arrayEqualIgnoreOrder(),
});

export const datetimeRangeSegmentEqual = objectEqual<DatetimeRangeSegment>({
  segmentableCharacteristic: stringEqual,
  startDateTime: stringEqual,
  endDateTime: stringEqual,
  ianaTimezone: stringEqual,
});

export const checkoutProbabilityExpressionEqual = arrayEqualIgnoreOrder<
  CheckoutProbabilitySegment["expressions"][number]
>((expr) => `${expr.probability}:${expr.operator}`);

export const checkoutProbabilitySegmentEqual =
  objectEqual<CheckoutProbabilitySegment>({
    segmentableCharacteristic: stringEqual,
    expressions: checkoutProbabilityExpressionEqual,
  });

export const productCollectionsSegmentEqual =
  objectEqual<ProductCollectionsSegment>({
    segmentableCharacteristic: stringEqual,
    collectionIds: arrayEqualIgnoreOrder(),
    collectionListOperator: stringEqual,
    cartOperator: stringEqual,
  });

export const lineItemPropertiesSegmentEqual =
  objectEqual<LineItemPropertiesSegment>({
    segmentableCharacteristic: stringEqual,
    properties: arrayEqualIgnoreOrder(
      (prop) => `${prop.key}:${prop.value}:${prop.matchMode ?? "exact"}`,
    ),
    propertiesListOperator: stringEqual,
    cartOperator: stringEqual,
  });

export const productMetafieldsSegmentEqual =
  objectEqual<ProductMetafieldsSegment>({
    segmentableCharacteristic: stringEqual,
    metafields: arrayEqualIgnoreOrder(
      (metafield) =>
        `${metafield.resourceType}:${metafield.namespace}.${metafield.key}=${metafield.value}:${metafield.valueMatchKind || "exact"}`,
    ),
    operator: stringEqual,
    cartOperator: stringEqual,
  });

export const cartValueSegmentEqual = objectEqual<CartValueSegment>({
  segmentableCharacteristic: stringEqual,
  value: objectEqual({ amount: numberEqual }),
  operator: stringEqual,
  valueKind: stringEqual,
});

export const shipmentWeightSegmentEqual = objectEqual<ShipmentWeightSegment>({
  segmentableCharacteristic: stringEqual,
  weight: numberEqual,
  unit: stringEqual,
  operator: stringEqual,
});

export const crossBorderShipmentSegmentEqual =
  objectEqual<CrossBorderShipmentSegment>({
    segmentableCharacteristic: stringEqual,
  });

export const poBoxShipmentSegmentEqual = objectEqual<PoBoxShipmentSegment>({
  segmentableCharacteristic: stringEqual,
});

export const addressPropertiesSegmentEqual =
  objectEqual<AddressPropertiesSegment>({
    segmentableCharacteristic: stringEqual,
    rules: arrayEqualIgnoreOrder<AddressPropertiesSegment["rules"][number]>(
      (rule) => `${rule.field}:${rule.operator}:${rule.value}`,
    ),
    conjunction: stringEqual,
  });

export const deliveryLocationRuleEqual = objectEqual<DeliveryLocationRule>({
  countryCode: stringEqual,
  provinces: optionalEqual(arrayEqualIgnoreOrder()),
});

export const deliveryLocationConditionEqual =
  objectEqual<DeliveryLocationCondition>({
    edgeId: stringEqual,
    locationRules: arrayEqualIgnoreOrder<DeliveryLocationRule>(
      (rule) =>
        `${rule.countryCode}:${(rule.provinces ?? []).sort().join(",")}`,
    ),
  });

export const deliveryLocationNarySegmentEqual =
  objectEqual<DeliveryLocationNarySegment>({
    segmentableCharacteristic: stringEqual,
    conditions: arrayEqualIgnoreOrder<DeliveryLocationCondition>(
      (condition) =>
        `${condition.edgeId}:${condition.locationRules
          .map(
            (r) => `${r.countryCode}:${(r.provinces ?? []).sort().join(",")}`,
          )
          .sort()
          .join(";")}`,
    ),
  });

export const deliveryLocationRulesSegmentEqual =
  objectEqual<DeliveryLocationRulesSegment>({
    segmentableCharacteristic: stringEqual,
    locationRules: arrayEqualIgnoreOrder<DeliveryLocationRule>(
      (rule) =>
        `${rule.countryCode}:${(rule.provinces ?? []).sort().join(",")}`,
    ),
    operator: stringEqual,
  });

/** Structural equality for any binary segment, dispatching on its characteristic. */
export function segmentEqual(a: Segment, b: Segment): boolean {
  switch (a.segmentableCharacteristic) {
    case SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES &&
        deliveryDistanceMilesSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.ORDER_COUNT:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.ORDER_COUNT &&
        orderCountSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.CUSTOMER_RETURN_RATE &&
        customerReturnRateSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.DELIVERY_LOCATION &&
        deliveryLocationSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.DELIVERY_LOCATION_RULES &&
        deliveryLocationRulesSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.CUSTOMER_TAGS:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.CUSTOMER_TAGS &&
        customerTagsSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_TAGS:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PRODUCT_TAGS &&
        productTagsSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_VENDOR:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PRODUCT_VENDOR &&
        productVendorSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_SKU:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PRODUCT_SKU &&
        productSkuSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.ITEM_QUANTITY:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.ITEM_QUANTITY &&
        itemQuantitySegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.ORIGIN_LOCATION:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.ORIGIN_LOCATION &&
        originLocationSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.TIME_OF_DAY:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.TIME_OF_DAY &&
        timeOfDaySegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.DATETIME_RANGE:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.DATETIME_RANGE &&
        datetimeRangeSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.CHECKOUT_PROBABILITY &&
        checkoutProbabilitySegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PRODUCT_COLLECTIONS &&
        productCollectionsSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.LINE_ITEM_PROPERTIES &&
        lineItemPropertiesSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PRODUCT_METAFIELDS &&
        productMetafieldsSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.CART_VALUE:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.CART_VALUE &&
        cartValueSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.SHIPMENT_WEIGHT &&
        shipmentWeightSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.CROSS_BORDER_SHIPMENT &&
        crossBorderShipmentSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.PO_BOX_SHIPMENT &&
        poBoxShipmentSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.ADDRESS_PROPERTIES &&
        addressPropertiesSegmentEqual(a, b)
      );
    case SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE:
      return (
        b.segmentableCharacteristic ===
          SegmentableCheckoutCharacteristic.HISTORICAL_ORDER_VALUE &&
        historicalOrderValueSegmentEqual(a, b)
      );
    default:
      return assertNever(a);
  }
}

const compoundSegmentLeafEqual = (
  a: CompoundSegmentLeaf,
  b: CompoundSegmentLeaf,
): boolean => a.negate === b.negate && segmentEqual(a.segment, b.segment);

const compoundSegmentGroupEqual = (
  a: CompoundSegmentGroup,
  b: CompoundSegmentGroup,
): boolean =>
  a.conjunction === b.conjunction &&
  a.leaves.length === b.leaves.length &&
  a.leaves.every((leaf, index) => {
    const other = b.leaves[index];
    return other !== undefined && compoundSegmentLeafEqual(leaf, other);
  });

const compoundSegmentConditionEqual = (
  a: CompoundSegmentCondition,
  b: CompoundSegmentCondition,
): boolean =>
  a.conjunction === b.conjunction &&
  a.groups.length === b.groups.length &&
  a.groups.every((group, index) => {
    const other = b.groups[index];
    return other !== undefined && compoundSegmentGroupEqual(group, other);
  });

/** Position of the edge an id refers to, or -1 when the reference dangles. */
function narySegmentEdgeIndex(
  node: CheckoutTreeNarySegmentNode,
  edgeId: string,
): number {
  return node.edges.findIndex(
    (edge) => edge._id.toString() === edgeId.toString(),
  );
}

/**
 * Ids are regenerated when a subtree is copied, so edge references are compared by
 * resolved position rather than value. Condition order is evaluation precedence.
 */
export function compoundNarySegmentNodeEqual(
  a: CheckoutTreeNarySegmentNode,
  b: CheckoutTreeNarySegmentNode,
): boolean {
  const segmentA = a.segment;
  const segmentB = b.segment;
  if (
    segmentA.segmentableCharacteristic !==
      SegmentableCheckoutCharacteristic.COMPOUND ||
    segmentB.segmentableCharacteristic !==
      SegmentableCheckoutCharacteristic.COMPOUND
  ) {
    return false;
  }
  if (
    a.edges.length !== b.edges.length ||
    narySegmentEdgeIndex(a, a.defaultEdgeId) !==
      narySegmentEdgeIndex(b, b.defaultEdgeId) ||
    segmentA.conditions.length !== segmentB.conditions.length
  ) {
    return false;
  }
  return segmentA.conditions.every((condition, index) => {
    const other = segmentB.conditions[index];
    return (
      other !== undefined &&
      narySegmentEdgeIndex(a, condition.edgeId) ===
        narySegmentEdgeIndex(b, other.edgeId) &&
      compoundSegmentConditionEqual(condition, other)
    );
  });
}
